// src/settings/AnnouncementPage.jsx
'use client';

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Container,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Box,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NotificationsIcon from '@mui/icons-material/Notifications';
import GroupIcon from '@mui/icons-material/Group';
import TitleIcon from '@mui/icons-material/Title';
import MessageIcon from '@mui/icons-material/Message';

import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from './components/SettingsMenu';
import useSettingsStyles from './common/useSettingsStyles';
import fetchOrThrow from '../common/util/fetchOrThrow';
import SelectField from '../common/components/SelectField';
import { prefixString } from '../common/util/stringUtils';

const AnnouncementPage = () => {
  const navigate = useNavigate();
  const { classes } = useSettingsStyles();
  const t = useTranslation();

  // Form state
  const [message, setMessage] = useState({ subject: '', body: '' });
  const [notificator, setNotificator] = useState();
  const [sendAll, setSendAll] = useState(false);

  // Users state
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersLoaded, setUsersLoaded] = useState(false);

  // Lazy-load users
  const loadUsers = async () => {
    if (usersLoaded || sendAll) return;
    setLoadingUsers(true);
    try {
      const res = await fetchOrThrow('/api/users?excludeAttributes=true');
      const data = await res.json();
      const normalized = data.map((u) => ({
        id: u.id,
        name: u.name || u.email || String(u.id),
        email: u.email,
      }));
      normalized.sort((a, b) => a.name.localeCompare(b.name));
      setUsers(normalized);
      setUsersLoaded(true);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSend = async () => {
    if (!notificator || !message.subject || !message.body) return;

    const query = new URLSearchParams();
    if (!sendAll) {
      selectedUsers.forEach((u) => query.append('userId', u.id));
    }

    const payload = {
      subject: message.subject.trim(),
      body: message.body.trim(),
    };

    await fetchOrThrow(
      `/api/notifications/send/${notificator}?${query.toString()}${sendAll ? '&all=true' : ''}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    );

    navigate(-1);
  };

  const canSend =
    !!notificator &&
    !!message.subject?.trim() &&
    !!message.body?.trim() &&
    (sendAll || selectedUsers.length > 0);

  // Reaproveita estilos do UserPage.jsx
  const summarySx = {
    px: 2,
    py: 1,
    bgcolor: 'background.paper',
  };
  const detailsSx = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    p: 2,
  };

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['serverAnnouncement']}>
      <Container maxWidth="false" className={classes.container}>

        {/* ------------------------------------------------------------
            NOTIFICATOR / CANAIS
        ------------------------------------------------------------ */}
        <Accordion defaultExpanded disableGutters sx={{ mb: 2, borderRadius: 2, overflow: 'hidden', border: `1px solid`, borderColor: 'divider' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
            <Box display="flex" alignItems="center" gap={1.25}>
              <NotificationsIcon sx={{ color: 'primary.main' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {t('notificationNotificators')}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails className={classes.details} sx={detailsSx}>
            <SelectField
              value={notificator}
              onChange={(e) => setNotificator(e.target.value)}
              endpoint="/api/notifications/notificators?announcement=true"
              keyGetter={(it) => it.type}
              titleGetter={(it) => t(prefixString('notificator', it.type))}
              label={t('notificationNotificators')}
            />
          </AccordionDetails>
        </Accordion>

        {/* ------------------------------------------------------------
            USUÁRIOS
        ------------------------------------------------------------ */}
        <Accordion disableGutters sx={{ mb: 2, borderRadius: 2, overflow: 'hidden', border: `1px solid`, borderColor: 'divider' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
            <Box display="flex" alignItems="center" gap={1.25}>
              <GroupIcon sx={{ color: 'primary.main' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {t('settingsUsers')}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails className={classes.details} sx={detailsSx}>
            <FormControlLabel
              control={<Checkbox checked={sendAll} onChange={(e) => setSendAll(e.target.checked)} />}
              label={t('announcementSendAll') || 'Enviar para todos os usuários'}
            />
            <Box sx={{ width: '100%', opacity: sendAll ? 0.5 : 1 }}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                disabled={sendAll}
                options={users}
                loading={loadingUsers}
                value={selectedUsers}
                onChange={(_, v) => setSelectedUsers(v)}
                onOpen={loadUsers}
                getOptionLabel={(o) => o.name}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => (
                  <TextField {...params} label={t('settingsUsers')} placeholder="Selecione..." />
                )}
              />
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* ------------------------------------------------------------
            ASSUNTO
        ------------------------------------------------------------ */}
        <Accordion disableGutters sx={{ mb: 2, borderRadius: 2, overflow: 'hidden', border: `1px solid`, borderColor: 'divider' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
            <Box display="flex" alignItems="center" gap={1.25}>
              <TitleIcon sx={{ color: 'primary.main' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {t('sharedSubject')}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails className={classes.details} sx={detailsSx}>
            <TextField
              value={message.subject}
              onChange={(e) => setMessage({ ...message, subject: e.target.value })}
              label={t('sharedSubject')}
            />
          </AccordionDetails>
        </Accordion>

        {/* ------------------------------------------------------------
            MENSAGEM
        ------------------------------------------------------------ */}
        <Accordion disableGutters sx={{ mb: 2, borderRadius: 2, overflow: 'hidden', border: `1px solid`, borderColor: 'divider' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
            <Box display="flex" alignItems="center" gap={1.25}>
              <MessageIcon sx={{ color: 'primary.main' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {t('commandMessage')}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails className={classes.details} sx={detailsSx}>
            <TextField
              value={message.body}
              onChange={(e) => setMessage({ ...message, body: e.target.value })}
              label={t('commandMessage')}
              multiline
              minRows={4}
            />
          </AccordionDetails>
        </Accordion>

        {/* ------------------------------------------------------------
            BOTÕES
        ------------------------------------------------------------ */}
        <div className={classes.buttons}>
          <Button
            type="button"
            color="primary"
            variant="outlined"
            onClick={() => navigate(-1)}
          >
            {t('sharedCancel')}
          </Button>
          <Button
            type="button"
            color="primary"
            variant="contained"
            onClick={handleSend}
            disabled={!canSend}
          >
            {t('commandSend')}
          </Button>
        </div>

      </Container>
    </PageLayout>
  );
};

export default AnnouncementPage;
